
import { supabase } from '@/integrations/supabase/client';
import { CommentWithVotes, CommentSortOption } from '../types';
import { applySorting } from './helpers';

/**
 * Fetch comments for a poll with proper profile joins
 * @param id The poll ID
 * @param limit Maximum number of comments to return
 * @param sortBy Sort option (newest, oldest, votes)
 * @param parentId Optional parent ID for fetching replies (null for top-level comments)
 * @returns Array of comments with vote counts
 */
export async function fetchPollComments(
  id: string,
  limit: number = 50,
  sortBy: CommentSortOption = 'newest',
  parentId: string | null = null
): Promise<CommentWithVotes[]> {
  try {
    console.log(`Fetching ${parentId ? 'replies' : 'comments'} for poll ${id || 'n/a'} with limit ${limit}, sorted by ${sortBy}, parent: ${parentId || 'null'}`);

    // Build the query with proper profile joins
    let query = supabase
      .from('poll_comments')
      .select(`
        id,
        poll_id,
        user_id,
        anonymous_id,
        content,
        parent_id,
        created_at,
        updated_at,
        deleted,
        reported,
        profile:profiles(
          id,
          name,
          avatar_url
        ),
        comment_votes:poll_comment_votes(
          id,
          vote_type
        )
      `)
      .is('deleted', false);
    
    // Add filtering based on whether we're fetching top-level comments or replies
    if (parentId === null) {
      // For top-level comments, filter by poll_id and null parent_id
      if (id) {
        query = query.eq('poll_id', id);
      }
      query = query.is('parent_id', null);
    } else {
      // For replies, filter by parent_id
      query = query.eq('parent_id', parentId);
    }
    
    // Add sorting
    query = applySorting(query, sortBy);

    // Add limit to the query
    query = query.limit(limit);
    
    // Execute the query
    const { data, error } = await query;
    
    if (error) {
      console.error('Error in fetchPollComments Supabase query:', error);
      throw new Error(error.message);
    }
    
    if (!data) {
      console.log('No data returned from fetchPollComments');
      return [];
    }

    console.log(`Poll ${parentId ? 'replies' : 'comments'} retrieved: ${data.length}`);
    
    // Transform the data to include vote counts
    const commentsWithVotes = data.map(async (comment: any) => {
      const upvotes = comment.comment_votes?.filter((v: any) => v.vote_type === 'up').length || 0;
      const downvotes = comment.comment_votes?.filter((v: any) => v.vote_type === 'down').length || 0;
      
      // Count replies for this comment
      let reply_count = 0;
      try {
        const { count } = await supabase
          .from('poll_comments')
          .select('id', { count: 'exact', head: true })
          .eq('parent_id', comment.id)
          .is('deleted', false);
          
        reply_count = count || 0;
      } catch (err) {
        console.log('Error counting replies:', err);
      }
      
      return {
        id: comment.id,
        poll_id: comment.poll_id,
        user_id: comment.user_id,
        anonymous_id: comment.anonymous_id,
        content: comment.content,
        parent_id: comment.parent_id,
        created_at: comment.created_at,
        updated_at: comment.updated_at,
        deleted: comment.deleted,
        reported: comment.reported,
        upvotes,
        downvotes,
        profile: comment.profile || { id: null, name: 'Anonymous User', avatar_url: null },
        reply_count
      };
    });
    
    // Wait for all profile fetches to complete
    return await Promise.all(commentsWithVotes);
    
  } catch (error) {
    console.error('Error fetchPollComments:', error);
    return [];
  }
}
